import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageBanner } from "@/components/reusable";
import { useTelemetry } from "@/hooks/useTelemetry";
import { AnalyticsFilterProvider, useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import { fetchAnalyticsSummary, fetchCoverholderOptions } from "@/services/analyticsApi";
import type { AnalyticsSummary, CoverholderOption } from "@/types/analytics";

// Layout + filters
import { AnalyticsHeader } from "@/components/analytics/AnalyticsHeader";
import { AnalyticsFilters } from "@/components/analytics/AnalyticsFilters";
import { MetricsStrip } from "@/components/analytics/MetricsStrip";

// Sample-derived panels
import { SiByTypeStackedBar } from "@/components/analytics/SiByTypeStackedBar";
import { FleetByFlagMap } from "@/components/analytics/FleetByFlagMap";
import { DqFailureBreakdown } from "@/components/analytics/DqFailureBreakdown";
import { VesselsByYearBar } from "@/components/analytics/VesselsByYearBar";
import { ClassSocietyDonut } from "@/components/analytics/ClassSocietyDonut";
import { WarRiskExposure } from "@/components/analytics/WarRiskExposure";
import { DeductibleStructure } from "@/components/analytics/DeductibleStructure";
import { FieldCompletenessGrid } from "@/components/analytics/FieldCompletenessGrid";
import { VesselReviewQueue } from "@/components/analytics/VesselReviewQueue";

// ROI / processing-side panels
import { WorkDoneCounters } from "@/components/analytics/WorkDoneCounters";
import { AutomationRateStream } from "@/components/analytics/AutomationRateStream";
import { KbGrowthChart } from "@/components/analytics/KbGrowthChart";
import { HumanAttentionRings } from "@/components/analytics/HumanAttentionRings";
import { BeforeAfterDrilldown } from "@/components/analytics/BeforeAfterDrilldown";

export function AnalyticsPage() {
  const { t } = useTranslation();

  return (
    <>
      <PageBanner title={t("analytics.title")} subtitle={t("analytics.subtitle")} />
      <AnalyticsFilterProvider>
        <AnalyticsBody />
      </AnalyticsFilterProvider>
    </>
  );
}

/**
 * Body split out so the filter context is available. Lives inside
 * `AnalyticsFilterProvider`; reads filters via `useAnalyticsFilters`.
 */
function AnalyticsBody() {
  const { t } = useTranslation();
  const { track, trackError, startTimer } = useTelemetry();
  const filters = useAnalyticsFilters();

  const [coverholders, setCoverholders] = useState<CoverholderOption[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Coverholders load once.
  useEffect(() => {
    const stopTimer = startTimer();
    fetchCoverholderOptions()
      .then((opts) => {
        setCoverholders(opts);
        track("analytics_coverholders_loaded", {
          count: opts.length,
          durationMs: stopTimer(),
        });
      })
      .catch((err: unknown) =>
        trackError(err instanceof Error ? err : new Error(String(err)), {
          context: "analytics_coverholders",
        })
      );
  }, [startTimer, track, trackError]);

  // Summary re-fetches whenever filters change.
  useEffect(() => {
    let active = true;
    setSummaryLoading(true);
    fetchAnalyticsSummary(filters)
      .then((s) => {
        if (!active) return;
        setSummary(s);
      })
      .catch((err: unknown) =>
        trackError(err instanceof Error ? err : new Error(String(err)), {
          context: "analytics_summary",
        })
      )
      .finally(() => {
        if (active) setSummaryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters, trackError]);

  const handleRefresh = useCallback(() => {
    track("analytics_refresh_clicked", { coverholder: filters.coverholder });
    // Bounce the summary to simulate a reload. Charts pick up new data via
    // their own effects — they do not currently subscribe to a refresh signal.
    setSummaryLoading(true);
    fetchAnalyticsSummary(filters)
      .then((s) => setSummary(s))
      .finally(() => setSummaryLoading(false));
  }, [filters, track]);

  const handleExport = useCallback(() => {
    track("analytics_export_clicked", { coverholder: filters.coverholder });
    // Wiring: when the analytics export endpoint lands, swap for the real call.

    alert("Export not wired yet — backend endpoint pending.");
  }, [filters, track]);

  return (
    <div className="animate-fade-in">
      <AnalyticsHeader
        summary={summary}
        loading={summaryLoading}
        onRefresh={handleRefresh}
        onExport={handleExport}
      />

      <AnalyticsFilters coverholders={coverholders} />

      <MetricsStrip summary={summary} loading={summaryLoading} />

      {/* ── Portfolio exposure ── */}
      <SectionLabel>{t("analytics.sections.portfolio")}</SectionLabel>
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 mb-6">
        <SiByTypeStackedBar />
        <FleetByFlagMap />
      </div>

      {/* ── Fleet composition ── */}
      <SectionLabel>{t("analytics.sections.fleet")}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <DqFailureBreakdown />
        <VesselsByYearBar />
        <ClassSocietyDonut />
      </div>

      {/* ── Risk structure ── */}
      <SectionLabel>{t("analytics.sections.risk")}</SectionLabel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <WarRiskExposure />
        <DeductibleStructure />
      </div>

      {/* ── Schedule completeness ── */}
      <SectionLabel>{t("analytics.sections.completeness")}</SectionLabel>
      <div className="mb-6">
        <FieldCompletenessGrid />
      </div>

      {/* ── Review queue ── */}
      <SectionLabel>{t("analytics.sections.reviewQueue")}</SectionLabel>
      <div className="mb-8">
        <VesselReviewQueue />
      </div>

      {/* ── Processing & automation insight ── */}
      <SectionLabel>{t("analytics.sections.processing")}</SectionLabel>
      <div className="mb-4">
        <WorkDoneCounters />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <AutomationRateStream />
        <KbGrowthChart />
      </div>

      {/* ── Operational insight ── */}
      <SectionLabel>{t("analytics.sections.operational")}</SectionLabel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <HumanAttentionRings />
        <BeforeAfterDrilldown />
      </div>

      <AnalyticsFooter summary={summary} />
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-tertiary)] mb-2 mt-1">
      {children}
    </div>
  );
}

function AnalyticsFooter({ summary }: { summary: AnalyticsSummary | null }) {
  const { t } = useTranslation();
  if (!summary) return null;
  const imported = new Date(summary.lastImportedAt);
  const dateStr = Number.isNaN(imported.getTime())
    ? summary.lastImportedAt
    : imported.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
  return (
    <div className="text-[11px] text-[var(--color-text-tertiary)] text-right py-2">
      {t("analytics.footer.lastImported")}: {dateStr} &nbsp;·&nbsp;{" "}
      {t("analytics.footer.coverholder")}: {summary.coverholderName}
    </div>
  );
}
